import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  CarWrapper,
  LocationWrapper,
  HoverWindow,
  Marker,
} from "commonComponents/HoleUI/CarsOnCourseStyled";
import { ReactComponent as OutOfServiceIcon } from "assets/svgIcons/MapLegendPopup/Status/Outofservice.svg";
import { isActionZoneAlert } from "_helperFunctions/isActionZoneAlert";
import { useTranslation } from "react-i18next";
import CarIcon from "../../assets/svgIcons/CarIcon";
import FlagIcon from "../../assets/svgIcons/FlagIcon";
import CloseIcon from "assets/svgIcons/ModalCloseOutIcon";
import BatteryIconWithCircle from "assets/svgIcons/BatteryIconWithCircle";
import BatteryIconWithCircleRed from "assets/svgIcons/BatteryIconWithCircleRed";
import MarshalCarIcon from "../../assets/svgIcons/CarType/Marshals";
import SpecialNeedCarIcons from "../../assets/svgIcons/CarType/SpecialNeeds";
import FoodandBeveragesCarIcon from "../../assets/svgIcons/CarType/F&B";
import ChatIcon from "assets/svgIcons/ChatIcon";
import ExclamationMarkWhiteIcon from "assets/svgIcons/ExclamationMarkIconWhite";
import ExclamationMarkWithRedBg from "assets/svgIcons/ExclamationMarkWithRedBg";
import TagIcon from "assets/svgIcons/LocationPointIcon";
import LocationPointIcon from "assets/svgIcons/ShareIcon";
import MapMarkerIcon from "assets/svgIcons/MapMarkerIcon";
import { useHookstate } from "@hookstate/core";
import EnergySaverIcon from "assets/images/energy-saver.png";
import CarNotCommunicatingImage from "assets/svgIcons/CarType/NotCommunication";
import { showEnergySaverIcon } from "_helperFunctions/showEnergySaverIcon";
import { batteryCritical } from "_helperFunctions/batteryCritical";
import { batteryWarning } from "_helperFunctions/batteryWarning";

import {
  MAP_GLOBAL_STATES,
  FLEET_GLOBAL_STATES,
  SITEID_GLOBAL_STATE,
  SITELOCATION_GLOBAL_STATE,
  CAR_LOCATION_STATE,
  CAR_NUMBER_MESSAGE,
  SiteSetupAction,
  UPDATED_CAR_DATA_STATE,
} from "globalStore/globalStore";

// for car property window import
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import ShowCenterIcon from "assets/svgIcons/ShowCenterIcon";
import ShowPointIcon from "assets/svgIcons/MapArrowMarkerIcon";
import {
  Tooltip,
} from "@mui/material";
import { getTextColorBasedOnRGB, teeMarkerColor } from "./teeMarkerColor";
import { mapHelpers } from "pages/Pins/utils/mapHelpers";
import { isStale } from "_helperFunctions/isStale";
import SignalIcon from "assets/svgIcons/SignalIcon";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";
import { reasonForReportString, carType as constCarType, PACE_STATUS_WARNING, PACE_STATUS_CRITICAL, controlType, deviceType } from "constant";
import { getPaceString } from "./functions/getPaceString";
import { useCarSettingsData } from "_helperFunctions/carSettings";
import CGB from "assets/icons/icon-circle-gray-blk.png";
import CGR from "assets/icons/icon-circle-gray-red.png";
import CGY from "assets/icons/icon-circle-gray-yell.png";
import WGR from "assets/icons/icon-wifi-gray-red.png";
import WGB from "assets/icons/icon-wifi-gray-blk.png";
import WGY from "assets/icons/icon-wifi-gray-yell.png";
import { createRoot } from "react-dom/client";

const MAP_ICON_COLORS = {
  PACE_WARNING: "#EAB52D",
  PACE_CRITICAL: "#FA3C30",
  PACE_NORMAL: "#F2F0E6", // Alabaster (old color was #947550)
  PACE_MEMBER_NORMAL: "#000000", // Alabaster (old color was #947550)
  POI_APPROACH: "#5E4DC3",
  POI_HOLE: "#483D8B"
};

const MapCenterMarker = (props) => {
  const { uniqueId } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  return (
    <Marker
      key={uniqueId}
      className="marker"
      style={{ cursor: !enableClickToDriveFeature.get() && "pointer" }}
    >
      <ShowCenterIcon />
    </Marker>
  );
};

const MapPointMarker = (props) => {
  const { uniqueId, type, position } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);

  const renderPoiIcons = () => { // the white arrows inside the markers
    if (position === 0) {
      return (
        <ShowPointIcon
          style={{
            transform: "rotate(90deg)",
            position: "relative",
            zIndex: "1",
          }}
        />
      );
    } else if (position === 1) {
      return (
        <ShowPointIcon
          style={{
            transform: "rotate(270deg)",
            position: "relative",
            zIndex: "1",
          }}
        />
      );
    } else if (position === 2) {
      return (
        <ShowPointIcon
          style={{
            transform: "rotate(180deg)",
            position: "relative",
            zIndex: "1",
          }}
        />
      );
    } else if (position === 3) {
      return (
        <ShowPointIcon
          style={{ position: "relative", zIndex: "1" }}
        />
      );
    }
  };

  return (
    <div
      key={uniqueId}
      className="marker"
      style={{
        cursor: !enableClickToDriveFeature.get() && "pointer",
        position: "relative",
      }}
    >
      {renderPoiIcons()}
      <MapMarkerIcon
        color={type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH}
        style={{
          position: "absolute",
          top: "-9px",
          left: "-9px",
          right: "0",
          zIndex: "0",
          color: type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH,
        }}
      />
    </div>
  );
};

const MapTeesMarker = (props) => {
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  const { uniqueId, holeNumber, teeMarkerSeetingId } = props;

  const { siteSetupData } = useSelector((state) => state.commonReducers) || [];

  return (
    <div
      key={uniqueId}
      className="marker"
      style={{
        cursor: !enableClickToDriveFeature.get() && "pointer",
        position: "relative",
      }}
    >
      <MapMarkerIcon
        color={teeMarkerColor(siteSetupData, teeMarkerSeetingId)}
      />
      <div
        style={{
          backgroundColor: teeMarkerColor(siteSetupData, teeMarkerSeetingId),
          width: "23px",
          height: "19px",
          borderRadius: "5px",
          fontSize: "16px",
          lineHeight: "19px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          position: "absolute",
          bottom: "13px",
          color: getTextColorBasedOnRGB(siteSetupData, teeMarkerSeetingId),
        }}
      >
        {holeNumber}
      </div>
    </div>
  );
};

const MapHoleMarker = (props) => {
  const { uniqueId, courseNumber, holeNumber } = props;

  const { siteSettingsData } =
    useSelector((state) => state.commonReducers) || [];
  return (
    <div
      key={uniqueId}
      className="marker"
      style={{
        backgroundColor:
          siteSettingsData &&
          mapHelpers.CourseBGColor(siteSettingsData, courseNumber),
        width: 22,
        height: 22,
        borderRadius: 50,
        fontSize: "13px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color:
          siteSettingsData &&
          mapHelpers.CourseTxtColor(siteSettingsData, courseNumber),
        fontWeight: "bold",
        border: "1px solid",
        borderColor:
          courseNumber === 1 ? "#fff" : courseNumber === 2 ? "#000" : "white",
      }}
    >
      {holeNumber}
    </div>
  );
};

export const NewMapPointMarker = (props) => {
  const { uniqueId, type, position } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  const renderPoiIcons = () => { // the white arrows inside the markers
    if (position === 0) {
      return (
        <ShowPointIcon
          style={{ transform: "rotate(90deg)" }}
        />
      );
    } else if (position === 1) {
      return (
        <ShowPointIcon
          style={{ transform: "rotate(270deg)" }}
        />
      );
    } else if (position === 2) {
      return (
        <ShowPointIcon
          style={{ transform: "rotate(180deg)" }}
        />
      );
    } else if (position === 3) {
      return <ShowPointIcon />;
    }
  };

  return (
    <div
      key={uniqueId}
      className="marker"
      style={{ cursor: !enableClickToDriveFeature.get() && "pointer" }}
    >
      {renderPoiIcons()}
      <MapMarkerIcon
        color={type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH}
        style={{
          position: "absolute",
          top: "-9px",
          left: "-9px",
          right: "0",
          zIndex: "0",
          color: type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH,
        }}
      />
    </div>
  );
};

export const NewMapTeesMarker = (props) => {
  const { uniqueId, teeMarkerSeetingId, holeNumber } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  const { siteSetupData } = useSelector((state) => state.commonReducers) || [];
  return (
    <div
      key={uniqueId}
      className="marker"
      style={{ cursor: !enableClickToDriveFeature.get() && "pointer", position: "relative", }}
    >
      <MapMarkerIcon
        color={teeMarkerColor(siteSetupData, teeMarkerSeetingId)}
      />
      <div
        style={{
          backgroundColor: teeMarkerColor(siteSetupData, teeMarkerSeetingId),
          width: "23px",
          height: "19px",
          borderRadius: "5px",
          fontSize: "16px",
          lineHeight: "19px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          position: "absolute",
          bottom: "13px",
          color: getTextColorBasedOnRGB(siteSetupData, teeMarkerSeetingId),
        }}
      >
        {holeNumber}
      </div>
    </div>
  );
};

export const LocationIcon = ({ Id, locationName, IsEUSite }) => {
  const { siteId, selectSite, hideSubNav } = useHookstate(SITEID_GLOBAL_STATE);
  const { siteLocationGlobalData, singleSiteLocationData, recentSiteState } = useHookstate(SITELOCATION_GLOBAL_STATE);
  const { hideAll } = useHookstate(SiteSetupAction);

  const locationZoom = () => {
    siteId.set(Id);
    hideSubNav.set(true);
    recentSiteState.set(false);
    localStorage.setItem("recentSite", Id);
    siteLocationGlobalData.set(false);
    singleSiteLocationData.set(true);
    selectSite.set(false);
    hideAll.set(false);
  };

  return (
    <LocationWrapper key={Id}>
      <div
        title={locationName + " - " + Id}
        onClick={(e) => locationZoom(e)}
        className="container1"
        id={Id}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            lineHeight: "23px",
          }}
        >
          <button
            style={{
              background: IsEUSite ? "#FFD500" : "#ffffff",
            }}
          >
            <FlagIcon color={"#000000"} />
          </button>
        </div>
      </div>
    </LocationWrapper>
  );
};

const MapCarIcon = forwardRef(({
  carItem,
  uniqueId,
  setEditModel,
  mapsData,
  mapData,
  overlapData,
  carProductData,
  setMarkerHovered
}, ref) => {
  const {
    showMessageBoxForSingleCar,
    getSingleCarData,
    tagCarNumber,
    highlightTagCar,
    carForPopup,
    taggedCarsSaverGlobalState,
    carDetailGpsiCarId,
    carNumberHover,
    taggedCarsDisplayerGlobalState,
    showCarDetails,
    clickWindowState,
    clickWindowStateCarNumber,
    showTaggedCarArray,
    TagCar,
    clientXOnClick,
    clientYOnClick,
    specificCarTrackHistory,
    showTaggedCar,
    displayLocation,
    carPopupClickEvent,
    cometTrailOnHoverGlobalState,
    mapSideNavTabState
  } = useHookstate(MAP_GLOBAL_STATES);

  const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

  const { updatedCarData } = useHookstate(UPDATED_CAR_DATA_STATE);
  const {
    siteSettingsData,
    getCarPathActionZone,
    getCarTracksData,
  } = useSelector((state) => state.commonReducers) || [];

  const { carLocationBtn } = useHookstate(CAR_LOCATION_STATE);
  const dispatch = useDispatch();
  const { isSiteUsingYards, convertMileToKM } = useCarSettingsData();

  const carMarkerRef = useRef(null);
  const hoverOverlayRef = useRef(null);
  const clickOverlayRef = useRef(null);

  const { t } = useTranslation();

  const { setCarNumber, carsGpsSelectorData, carsForMessagesGetter } = useHookstate(CAR_NUMBER_MESSAGE);

  const [carPopupClass, setCarPopupClass] = useState("");
  const [hoverState, setHoverState] = useState(false);

  const [polyLine, setPolyLine] = useState({});
  const [carTrailMarkers, setCarTrailMarkers] = useState([]);

  const memberNames = carItem?.Car?.CarType === constCarType.member && carItem?.Members && carItem?.Members.length > 0 ? { memberName1: carItem?.Members[0]?.MemberName, memberName2: carItem?.Members[0]?.MemberName2 } : "";
  // Expose ref for parent to use
  useImperativeHandle(ref, () => ({
    carMarkerRef
  }));

  useEffect(() => {
    const localData = JSON.parse(localStorage.getItem("taggedCarsData"));
    if (localData) {
      taggedCarsSaverGlobalState.set(localData.tagCarsDataMap);
      showTaggedCarArray.set(localData.tagCarsDataFilters);
      taggedCarsDisplayerGlobalState.set(localData.taggedCarsDisplayerGlobalState);
    }
  }, []);
  useEffect(() => {
    const parsedCarData = updatedCarData.get() && JSON.parse(updatedCarData.get())?.length ? JSON.parse(updatedCarData.get()) : [];

    if (Array.isArray(parsedCarData) && parsedCarData && parsedCarData.length > 0 && carDetailGpsiCarId.get()) {
      const selectedCar = parsedCarData.find((item) => item?.Car?.GpsiCarId === carDetailGpsiCarId.get());
      if (selectedCar) {
        carForPopup.set(selectedCar);
      }
    } else {
      carForPopup.set({});
    }

    hideHoverOverlay();
  }, [updatedCarData.get(), carDetailGpsiCarId.get()]);

  useEffect(() => {
    carMarkerRef.current.className = " container";
    taggedCarsSaverGlobalState.get().map((item) => {
      const carElement = document.getElementById("id" + item);
      if (carElement) carElement.className = "tag-car container";
    });
  }, [taggedCarsSaverGlobalState.get()]);

  useEffect(() => {
    if (Object.keys(polyLine).length !== 0) {
      polyLine.setMap(mapData);
    }

    return () => {
      if (Object.keys(polyLine).length !== 0) {
        polyLine.setMap(null);
      }
    };
  }, [polyLine, mapData]);

  useEffect(() => {
    if (clickWindowState.get()) fixPosition(null);
  }, [carPopupClass]);

  // Helper function to get odometer display

  const getOdometerDisplay = (carItem) => {
    if (!carProductData) return "";

    const odometerBase = carItem?.GPSOdometerBase ?? 0;
    let odometer;

    if (
      carProductData.CarHistoryOdometer !== null &&
      carProductData.CarHistoryOdometer !== undefined &&
      carProductData.CarHistoryOdometer !== 0
    ) {
      odometer = carProductData.CarHistoryOdometer;
    } else if (carProductData.DevType === deviceType.FT2) {
      odometer = carProductData.Odometer;
    } else if (!carItem?.CanCarCommunicate) {
      odometer = carProductData.GPSOdometer;
    } else {
      odometer = carProductData.Odometer ?? carProductData.GPSOdometer ?? 0;
    }

    if (carItem?.CarControlType === controlType.None) {
      odometer = (odometer ?? 0) + odometerBase;
    }

    const isMiles = isSiteUsingYards();
    if (isMiles) {
      const miles = Math.round(odometer);
      return `${miles} ${t(miles === 1 ? "Mile" : "Miles")}`;
    } else {
      const km = Math.round(convertMileToKM(odometer));
      return `${km} ${t(km === 1 ? "km" : "kms")}`;
    }
  };

  const getHrMeterDisplay = (carItem) => {
    if (!carProductData) return "";

    const hourMeterVal = carItem?.DevType === deviceType.FT2
      ? carProductData.CarHistoryHourMeter
      : carProductData.HourMeter;

    const hrMeter = Math.round(hourMeterVal + (carItem?.VirtualHourmeterBase ?? 0));
    return `${hrMeter} ${t(hrMeter === 1 ? "hour" : "hours")}`;
  };

  const getCarMakeModelWithYear = (carInformation) => {
    if (!carInformation?.CarMake && !carInformation?.CarModel && !carInformation.NonCommunicativeVehicle) return null;

    const fullSerial = carInformation?.FullSerialNumber;
    let year = carInformation?.NonCommunicativeVehicle?.CarYear || "";
    const carMake = carInformation?.CarMake || carInformation?.NonCommunicativeVehicle?.CarMake || "";
    const carModel = carInformation?.CarModel || carInformation?.NonCommunicativeVehicle?.CarModel || "";

    if (fullSerial && fullSerial.length >= 2) {
      const startIndex = fullSerial.length === 12 ? 1 : 2;
      const endIndex = startIndex + 2;
      // prepend 20 to populate year in "YYYY" format
      year = "20" + fullSerial.substring(startIndex, endIndex);
    }

    return `${year} ${carMake} ${carModel}`.trim();
  };

  const createHoverOverlayContent = () => {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.zIndex = "999999";
    div.style.pointerEvents = "none";
    div.style.transform = "translate(-50%, -100%)";

    const root = createRoot(div);

    root.render(
      <>
        {clickWindowStateCarNumber.get() !== carItem?.Car?.CarNumber &&
          <HoverWindow className={"carPopup " + carPopupClass}>
            <div className="SignalIconCover">
              <Tooltip title={t("This_car_is_connected_via_Wi-Fi")}>
                <div>
                  <SignalIcon
                    width="16px"
                    height="12.5px"
                    color={isStale(carItem?.Car) ? "gray" : "#5AADFF"}
                  />
                </div>
              </Tooltip>
            </div>
            {Array.isArray(overlapData?.overlappingDisplay) && overlapData?.overlappingDisplay.map((item, i) => {
              return (
                <div className="wrapper" key={"carHover" + i}>
                  <div style={{ fontSize: 20 }}>Car: {item.carNumber}</div>
                  <div style={{ fontSize: 13, fontWeight: "bold" }}>
                    {getCarMakeModelWithYear(item?.car)}
                  </div>
                  <div className="date-and-time" style={{ margin: '2px 2px' }}>
                    {moment
                      .utc(item?.car?.Car?.LastLocationUpdateInUtc)
                      .utcOffset(-siteSettingsData?.TimeZoneOffset)
                      .format("DD-MMM-YYYY, h:mm a")}
                  </div>
                  <>
                    <div style={{ display: "block", fontWeight: "bold", fontSize: 14 }}>
                      {(item?.car?.Car?.CarType === constCarType.member && item?.car?.Members && item?.car?.Members.length > 0) ? `${item?.car?.Members[0]?.MemberName} ${item?.car?.Members[0]?.MemberName2}` : ""}
                    </div>
                    <div>
                      <span style={{ fontSize: 14 }}>{handleHoleDescripton(item?.car?.Car)}</span>
                    </div>
                    <div>
                      {item?.car?.HoleLocation !== 0 ?
                        <>
                          <span style={{ fontWeight: "bold", fontSize: 14 }}>{t("Pace_Status")}</span>: &nbsp;
                          <span style={{ fontSize: 14 }}>{getPaceString(item.car?.Car)}</span>
                        </>
                        : ""}
                    </div>
                    <div style={{ fontSize: 14 }}>
                      {!(memberNames.memberName1 || memberNames.memberName2) &&
                        <>
                          <div>
                            {item.car?.Car?.PlayerName1 ? `Player Name - ${item.car?.Car?.PlayerName1}` : ""}
                          </div>
                          <div>
                            {item.car?.Car?.PlayerName2 ? `Player Name - ${item.car?.Car?.PlayerName2}` : ""}
                          </div>
                        </>
                      }
                      <div>
                        {reasonForReportString(item.car?.Car?.LocationReasonForReport)}
                      </div>
                      <div>
                        {t("GPRS_Signal")}: &nbsp;
                        {item.car?.Car?.LocationGprsSignalStrength}
                      </div>
                      <div>
                        {t("Sequence")}#: &nbsp;
                        {item.car?.Car?.LocationSequenceNumber}
                      </div>
                      {carProductData && (
                        <>
                          <div>
                            {t("Odometer")}: {getOdometerDisplay(item.car?.Car)}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {t("Hour_Meter")}: {getHrMeterDisplay(item.car?.Car)}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                  <div className="arrow"></div>
                </div>
              );
            })}
          </HoverWindow>
        }
      </>
    );

    div._reactRoot = root;

    return div;
  }

  const onMouseEnter = (e) => {
    if (Object.keys(polyLine).length !== 0) {
      polyLine.setMap(null);
      setPolyLine({});
    }

    if (carTrailMarkers && carTrailMarkers.length > 0) {
      carTrailMarkers.forEach((marker) => {
        marker.position = null;
      });
      setCarTrailMarkers([]);
    }

    carNumberHover.set(true);
    if (getCarTracksData && getCarTracksData.length > 0 && cometTrailOnHoverGlobalState.get() && mapSideNavTabState.get() == "Cars") {
      const carPositions = getCarTracksData.filter((data) => {
        return data.GpsiCarId === carItem?.Car?.GpsiCarId;
      });

      const sortedData = carPositions.sort((a, b) => {
        return new Date(a.date) - new Date(b.date); // Ascending order
      });

      const coordinates = sortedData.map((data) => {
        if (isValidLocation(data.Latitude, data.Longitude)) {
          addCarTrailMarker(data);
          return new mapsData.LatLng(
            data.Latitude,
            data.Longitude
          );
        }
      }).filter(Boolean); // Remove undefined values

      if (coordinates.length > 0) {
        const newPolyLine = new mapsData.Polyline({
          clickable: false,
          path: coordinates,
          strokeColor: "white",
          strokeOpacity: 1,
          strokeWeight: 3,
          zIndex: 0,
        });
        setPolyLine(newPolyLine);
      }
    }
    fixPosition(e);
    setHoverState(true);
  }

  const onMouseLeave = () => {
    carNumberHover.set(false);
    if (Object.keys(polyLine).length !== 0) {
      polyLine.setMap(null);
      setPolyLine({});
    }
    if (carTrailMarkers && carTrailMarkers.length > 0) {
      carTrailMarkers.forEach((marker) => {
        marker.position = null;
      });
      setCarTrailMarkers([]);
    }
    setHoverState(false);
  }

  const showHoverOverlay = (e) => {
    if (!mapData || hoverOverlayRef.current) return;

    setMarkerHovered(true);

    onMouseEnter(e);

    const contentDiv = createHoverOverlayContent();

    const position = new mapsData.LatLng(
      carItem.Car.Latitude,
      carItem.Car.Longitude
    );

    const overlay = new (class extends mapsData.OverlayView {
      constructor(position, contentDiv) {
        super();
        this.position = position;
        this.contentDiv = contentDiv;
        this.projection = null;
      }

      onAdd() {
        this.getPanes().floatPane.appendChild(this.contentDiv);
        this.projection = this.getProjection();

        // Force redraw AFTER projection exists
        window.google.maps.event.addListenerOnce(this, "projection_changed", () => {
          this.draw();
        });
      }

      draw() {
        if (!this.projection) {
          this.projection = this.getProjection();
          if (!this.projection) return;
        }

        const point = this.projection.fromLatLngToDivPixel(this.position);
        if (!point) return;

        this.contentDiv.style.left = `${point.x}px`;
        this.contentDiv.style.top = `${point.y}px`;
      }

      onRemove() {
        this.contentDiv?.parentNode?.removeChild(this.contentDiv);
        this.projection = null;
      }
    })(position, contentDiv);

    overlay.setMap(mapData);
    hoverOverlayRef.current = overlay;
  }

  const hideHoverOverlay = () => {
    if (hoverOverlayRef.current) {
      hoverOverlayRef.current.setMap(null);
      hoverOverlayRef.current = null;
    }

    setMarkerHovered(false);
    onMouseLeave();
  }

  const createClickOverlayContent = () => {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.zIndex = "999999";
    div.style.pointerEvents = "none";
    div.style.transform = "translate(-50%, -100%)";

    const root = createRoot(div);

    root.render(
      <>
        {clickWindowState.get() && clickWindowStateCarNumber.get() === carItem?.Car?.CarNumber &&
          <>
            {!showCarDetails.get() && (
              <HoverWindow
                className={carPopupClass}
                onClick={(e) => {
                  setCarPopupClickEvent(e);
                }}
              >
                <div className="SignalIconCover">
                  <SignalIcon
                    width="16px"
                    height="12.5px"
                    color={isStale(carItem?.Car) ? "gray" : "#5AADFF"}
                  />
                </div>
                <div className="wrapper">
                  <div style={{ fontSize: 20 }}>Car: {carItem?.Car?.CarNumber}</div>
                  <div style={{ fontSize: 13, fontWeight: "bold" }}>
                    {getCarMakeModelWithYear(carItem)}
                  </div>

                  <CloseIcon
                    className="close-icon"
                    onClick={handleCloseCarModal}
                  />

                  <div className="date-and-time" style={{ margin: '2px 2px' }}>
                    {moment
                      .utc(carItem?.Car?.LastLocationUpdateInUtc)
                      .utcOffset(-siteSettingsData?.TimeZoneOffset)
                      .format("DD-MMM-YYYY, h:mm a")}
                  </div>

                  <>
                    <div style={{ fontSize: 14, display: "block", fontWeight: "bold" }}>
                      {memberNames.memberName1} {memberNames.memberName2}
                    </div>
                    <div>
                      <span style={{ fontSize: 14 }}>{handleHoleDescripton(carItem?.Car)}</span>
                    </div>
                    <div>
                      {carItem?.Car?.HoleLocation !== 0 ?
                        <>
                          <span style={{ fontWeight: "bold", fontSize: 14 }}>{t("Pace_Status")}</span>: &nbsp;
                          <span style={{ fontSize: 14 }}>{getPaceString(carItem?.Car)}</span>
                        </>
                        : ""}
                    </div>
                    <div style={{ fontSize: 12 }}>
                      {!(memberNames.memberName1 || memberNames.memberName2) &&
                        <>
                          <div>
                            {carItem?.Car?.PlayerName1 ? `Player Name: ${carItem?.Car?.PlayerName1}` : ""}
                          </div>
                          <div>
                            {carItem?.Car?.PlayerName2 ? `Player Name: ${carItem?.Car?.PlayerName2}` : ""}
                          </div>
                        </>
                      }
                      <div>
                        {t("Report_Reason")}: &nbsp;
                        {reasonForReportString(carItem?.Car?.LocationReasonForReport)}
                      </div>
                      <div>
                        {t("GPRS_Signal")}: &nbsp;
                        {carItem?.Car?.LocationGprsSignalStrength}
                      </div>
                      <div>
                        {t("Sequence")}#: &nbsp;
                        {carItem?.Car?.LocationSequenceNumber}
                      </div>
                      {carProductData && (
                        <>
                          <div>
                            {t("Odometer")}: {getOdometerDisplay(carItem?.Car)}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {t("Hour_Meter")}: {getHrMeterDisplay(carItem?.Car)}
                          </div>
                        </>
                      )}
                    </div>
                  </>

                  <div className="icon-wrapper">
                    <Tooltip title={t("Send_Message")} placement="bottom">
                      <span onClick={(e) => { setCarsData(e) }}>
                        <ChatIcon
                          width={18}
                          height={14}
                          color="var(--text-primary)"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Show_detail")} placement="bottom">
                      <span>
                        <ExclamationMarkWhiteIcon
                          color="var(--text-primary)"
                          onClick={handleShowDetail}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Find_car")} placement="bottom">
                      <span className={makeHighlightTagOnMenu(carItem?.Car?.CarNumber) ? "active" : ""} onClick={handleFindCar}>
                        <TagIcon
                          color="var(--text-primary)"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Show/Hide_locations")} placement="bottom">
                      <span className={showTaggedCar.get() === carItem?.Car?.CarNumber ? "active" : ""} onClick={handleShowHideLocations}>
                        <LocationPointIcon color="var(--text-primary)" />
                      </span>
                    </Tooltip>
                  </div>

                  <div className="arrow"></div>
                </div>
              </HoverWindow>
            )}
          </>
        }
      </>
    );

    div._reactRoot = root;

    return div;
  }

  const showClickOverlay = () => {
    if (!mapData || clickOverlayRef.current) return;
    hideHoverOverlay();

    const contentDiv = createClickOverlayContent();

    const position = new mapsData.LatLng(
      carItem.Car.Latitude,
      carItem.Car.Longitude
    );

    const overlay = new (class extends mapsData.OverlayView {
      constructor(position, contentDiv) {
        super();
        this.position = position;
        this.contentDiv = contentDiv;
        this.projection = null;
      }

      onAdd() {
        this.getPanes().floatPane.appendChild(this.contentDiv);
        this.projection = this.getProjection();

        // Force redraw AFTER projection exists
        window.google.maps.event.addListenerOnce(this, "projection_changed", () => {
          this.draw();
        });
      }

      draw() {
        if (!this.projection) {
          this.projection = this.getProjection();
          if (!this.projection) return;
        }

        const point = this.projection.fromLatLngToDivPixel(this.position);
        if (!point) return;

        this.contentDiv.style.left = `${point.x}px`;
        this.contentDiv.style.top = `${point.y}px`;
      }

      onRemove() {
        this.contentDiv?.parentNode?.removeChild(this.contentDiv);
        this.projection = null;
      }
    })(position, contentDiv);

    overlay.setMap(mapData);
    clickOverlayRef.current = overlay;
  }

  const hideClickOverlay = () => {
    if (clickOverlayRef.current) {
      clickOverlayRef.current.setMap(null);
      clickOverlayRef.current = null;
    }
  }

  const setCarsData = (e) => {
    e.stopPropagation();
    setCarNumber.set(carItem?.Car?.CarNumber);
    getSingleCarData.set({ GpsiCarId: carItem?.Car?.GpsiCarId, CarNumber: carItem?.Car?.CarNumber });
    carsGpsSelectorData.set([carItem?.Car?.GpsiCarId]);
    carsForMessagesGetter.set([carItem?.Car?.CarNumber]);
    showMessageBoxForSingleCar.set(true);
    clickWindowState.set(false);
    clickWindowStateCarNumber.set(null);
  }

  const setTagCarNumber = (carnumber) => {
    if (tagCarNumber.get().length == 0) {
      tagCarNumber.set((tasks) => [...tasks, carnumber]);
    } else {
      const exists = tagCarNumber.get().find((item) => item == carnumber);
      if (exists) {
        tagCarNumber.set((tasks) =>
          tasks.filter((task) => task !== carnumber)
        );
      } else {
        tagCarNumber.set((tasks) => [...tasks, carnumber]);
      }
    }

    const localStorageData = JSON.parse(
      localStorage.getItem("taggedCarsData")
    );
    const showTaggedCarArrayExist = showTaggedCarArray
      .get()
      .find((item) => item === carnumber);
    if (showTaggedCarArrayExist) {
      showTaggedCarArray.set((tasks) =>
        tasks.filter((task) => task !== carnumber)
      );
      highlightTagCar.set(false);
    } else {
      showTaggedCarArray.set((tasks) => [...tasks, carnumber]);
      highlightTagCar.set(true);
    }

    const taggedCarsSaverGlobalStateExist = taggedCarsSaverGlobalState
      .get()
      .find((item) => item === carnumber);
    if (taggedCarsSaverGlobalStateExist) {
      taggedCarsSaverGlobalState.set((tasks) =>
        tasks.filter((task) => task !== carnumber)
      );
      taggedCarsDisplayerGlobalState.set(false);
      highlightTagCar.set(false);
    } else {
      taggedCarsSaverGlobalState.set((tasks) => [...tasks, carnumber]);
      taggedCarsDisplayerGlobalState.set(true);
      highlightTagCar.set(true);
    }

    if (localStorageData) {
      localStorage.setItem(
        "taggedCarsData",
        JSON.stringify({
          findCars: true,
          taggedCarsDisplayerGlobalState:
            taggedCarsDisplayerGlobalState.get(),
          tagCarsDataMap: taggedCarsSaverGlobalState.get(),
          tagCarsDataFilters: showTaggedCarArray.get(),
        })
      );
    } else {
      localStorage.setItem(
        "taggedCarsData",
        JSON.stringify({
          findCars: true,
          taggedCarsDisplayerGlobalState:
            taggedCarsDisplayerGlobalState.get(),
          tagCarsDataMap: taggedCarsSaverGlobalState.get(),
          tagCarsDataFilters: showTaggedCarArray.get(),
        })
      );
    }

    clickWindowState.set(false);
  }

  const makeHighlightTagOnMenu = (carNumber) => {
    let finded = taggedCarsSaverGlobalState
      .get()
      .find((item) => item === carNumber);
    if (!finded) {
      return false;
    } else {
      return true;
    }
  }

  const paceInfoBorder = (detector) => {
    if (detector === "border") {
      if (carItem?.Car && Object.keys(carItem?.Car).length !== 0) {
        const paceWarning = carItem?.Car?.PaceStatus ? carItem?.Car?.PaceStatus === PACE_STATUS_WARNING : null;
        const paceCritical = carItem?.Car?.PaceStatus ? carItem?.Car?.PaceStatus === PACE_STATUS_CRITICAL : null;
        if (paceWarning) {
          return MAP_ICON_COLORS.PACE_WARNING;
        }
        if (paceCritical) {
          return MAP_ICON_COLORS.PACE_CRITICAL;
        }

        return null;
      }
    } else if (detector === "car") { // doesn't look like paceInfoBorder("car") is ever called
      if (carItem?.Car && Object.keys(carItem?.Car).length !== 0) {
        const paceWarning = carItem?.Car?.PaceStatus ? carItem?.Car?.PaceStatus === PACE_STATUS_WARNING : null;
        const paceCritical = carItem?.Car?.PaceStatus ? carItem?.Car?.PaceStatus === PACE_STATUS_CRITICAL : null;

        let result = "";
        if (paceWarning && paceCritical) {
          if (paceWarning === 2) {
            result = `1px solid ${MAP_ICON_COLORS.PACE_WARNING}`;
          } else if (paceCritical === 3) {
            result = `1px solid ${MAP_ICON_COLORS.PACE_CRITICAL}`;
          } else {
            result = `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`;
          }
        }
        return result;
      }
    } else {
      return <></>;
    }
  }

  const showWindow = (e) => {
    setEditModel(false);
    carPopupClickEvent.set(e.target);
    clientXOnClick.set(e.clientX);
    clientYOnClick.set(e.clientY);
    if (hoverState) {
      setHoverState(false);
    }
    carNumberHover.set(true);
    clickWindowState.set(true);
    clickWindowStateCarNumber.set(carItem?.Car?.CarNumber);

    showClickOverlay();
  }

  const handleShowDetail = () => {
    showCarDetails.set(true);
    let temp = JSON.parse(JSON.stringify(carItem));
    carDetailGpsiCarId.set(temp.Car.GpsiCarId);
    carForPopup.set(temp);
  }

  const checkStyle = () => {
    if (tagCarNumber.get().includes(carItem?.Car?.CarNumber) || TagCar.get() === carItem?.Car?.CarNumber) {
      return "tag-car container";
    }
  }

  var minimumLongitude = -180.0;
  var maximumLongitude = 180.0;
  var minimumLatitude = -90.0;
  var maximumLatitude = 90.0;
  var oneKM = 0.01;
  var wifiIcon = {
    url: WGB,
    anchor: new mapsData.Point(11, 11), // 24 x 24 marker so anchor point 11,11 is right in the middle of the png
    size: new mapsData.Size(22, 22),
  };
  var wifiPaceWarningIcon = {
    url: WGY,
    anchor: new mapsData.Point(11, 11), // 24 x 24 marker so anchor point 11,11 is right in the middle of the png
    size: new mapsData.Size(22, 22),
  };
  var wifiPaceCriticalIcon = {
    url: WGR,
    anchor: new mapsData.Point(11, 11), // 24 x 24 marker so anchor point 11,11 is right in the middle of the png
    size: new mapsData.Size(22, 22),
  };
  var gprsIcon = {
    url: CGB,
    anchor: new mapsData.Point(11, 11), // 24 x 24 marker so anchor point 11,11 is right in the middle of the png
    size: new mapsData.Size(22, 22),
  };
  var gprsIconPaceWarning = {
    url: CGY,
    anchor: new mapsData.Point(11, 11), // 24 x 24 marker so anchor point 11,11 is right in the middle of the png
    size: new mapsData.Size(22, 22),
  };
  var gprsIconPaceCritical = {
    url: CGR,
    anchor: new mapsData.Point(11, 11), // 24 x 24 marker so anchor point 11,11 is right in the middle of the png
    size: new mapsData.Size(22, 22),
  };
  var isValidLatitude = (latitude) => {
    return latitude >= minimumLatitude && latitude <= maximumLatitude;
  };
  var isValidLongitude = (longitude) => {
    return longitude >= minimumLongitude && longitude <= maximumLongitude;
  };
  var isValidLocation = (latitude, longitude) => {
    if (Math.abs(latitude) < oneKM && Math.abs(longitude) < oneKM)
      return false;
    if (!isValidLatitude(latitude)) return false;
    if (!isValidLongitude(longitude)) return false;
    return true;
  };

  const createMarkerContent = (iconToUse) => {
    const imgElement = document.createElement('img');
    imgElement.src = iconToUse.url;
    imgElement.width = iconToUse.size.width;
    imgElement.height = iconToUse.size.height;
    imgElement.style.cursor = 'default';

    const wrapperDiv = document.createElement('div');
    wrapperDiv.style.position = 'absolute';
    wrapperDiv.style.transform = 'translate(-50%, -50%)';
    wrapperDiv.style.width = iconToUse.size.width + 'px';
    wrapperDiv.style.height = iconToUse.size.height + 'px';
    wrapperDiv.appendChild(imgElement);

    return wrapperDiv;
  }

  function addCarTrailMarker(carPosition) {
    if (!isValidLocation(carPosition.Latitude, carPosition.Longitude)) {
      return;
    }

    let iconToUse = carPosition.Network === 3 ? gprsIcon : wifiIcon;

    if (carPosition.PaceStatus !== 1) {
      const isWarning = carPosition.PaceStatus === 2;
      iconToUse = carPosition.Network === 3
        ? (isWarning ? gprsIconPaceWarning : gprsIconPaceCritical)
        : (isWarning ? wifiPaceWarningIcon : wifiPaceCriticalIcon);
    }

    const markerContent = createMarkerContent(iconToUse);

    setCarTrailMarkers((prev) => [
      ...prev,
      new window.google.maps.marker.AdvancedMarkerElement({
        position: new window.google.maps.LatLng(
          carPosition.Latitude,
          carPosition.Longitude
        ),
        map: mapData,
        content: markerContent,
        zIndex: 0,
        gmpDraggable: false,
      }),
    ]);
  }

  const fixPosition = (e) => {
    let xValue = 0;
    let yValue = 0;
    if (clickWindowState.get()) {
      xValue = clientXOnClick.get();
      yValue = clientYOnClick.get();
    } else {
      xValue = e.clientX;
      yValue = e.clientY;
    }

    if (yValue < 230 && xValue + 135 > window.innerWidth)
      //top-right side overflow
      setCarPopupClass("bottom-left");
    else if (yValue < 230 && xValue - 175 < 0)
      // top-left side overflow
      setCarPopupClass("bottom-right");
    else if (
      yValue + 230 > window.innerHeight &&
      xValue + 135 > window.innerWidth
    )
      // bottom-right side overflow
      setCarPopupClass("top-left");
    else if (yValue + 230 > window.innerHeight && xValue - 175 < 0)
      // bottom-left side overflow
      setCarPopupClass("top-right");
    else if (xValue + 135 > window.innerWidth)
      //right side overflow clientY
      setCarPopupClass("for-left");
    else if (xValue - 175 < 0)
      // left side overflow
      setCarPopupClass("for-right");
    else if (yValue + 230 > window.innerHeight)
      // bottom side overflow
      setCarPopupClass("for-top");
    else setCarPopupClass("");
  }

  const setCarPopupClickEvent = (e) => {
    if (e !== null) {
      carPopupClickEvent.set(e.target);
    } else {
      carPopupClickEvent.set(e);
    }
  }

  const handleShowHideLocations = () => {
    displayLocation.set((prevState) => !prevState);
    clickWindowState.set(false);

    localStorage.setItem("displayLocation", displayLocation.get());

    if (showTaggedCar.get() && showTaggedCar.get() === carItem?.Car?.CarNumber) {
      showTaggedCar.set(null);
      specificCarTrackHistory.set(false);
      carLocationBtn.set(false);

      let locationTrackingData = {
        carNumber: null,
        gpsiCarId: null,
        carType: null,
      };

      locationTrackingData = JSON.stringify(locationTrackingData);
      localStorage.setItem("locationTrackingData", locationTrackingData);
    } else {
      showTaggedCar.set(carItem?.Car?.CarNumber);

      let locationTrackingData = {
        carNumber: carItem?.Car?.CarNumber,
        gpsiCarId: carItem?.Car?.GpsiCarId,
        carType: carItem?.Car?.CarType,
      };

      locationTrackingData = JSON.stringify(locationTrackingData);
      localStorage.setItem("locationTrackingData", locationTrackingData);

      showTaggedCar.set(carItem?.Car?.CarNumber);
      let payload = {
        gpsiCarId: carItem?.Car?.GpsiCarId,
        hoursBack: "72",
      };

      dispatch(COMMON_SERVICES.getCarTrackServices(payload, siteId.get())).then(() => {
        specificCarTrackHistory.set(true);
        carLocationBtn.set(true);
      });
    }

    hideClickOverlay();
  }

  const handleFindCar = () => {
    setTagCarNumber(carItem?.Car?.CarNumber);
    let getTaggedCar = JSON.parse(localStorage.getItem("isTaggedCar"));

    if (getTaggedCar) {
      let tempToggle = getTaggedCar.state === true ? { state: false, carNumber: carItem?.Car?.CarNumber } : { state: true, carNumber: carItem?.Car?.CarNumber };
      localStorage.setItem("isTaggedCar", JSON.stringify(tempToggle));
    } else {
      let tempToggle = {
        state: true,
        carNumber: carItem?.Car?.CarNumber,
      };

      localStorage.setItem("isTaggedCar", JSON.stringify(tempToggle));
    }

    hideClickOverlay();
  }

  const handleHoleDescripton = (car) => {
    switch (car?.HoleLocation) {
      case 0:
        return "Not playing";
      case 1:
        return `Hole ${car?.Hole} Between Holes`;
      case 2:
        return `Hole ${car?.Hole} Tee`;
      case 3:
        return `Hole ${car?.Hole} Fairway`;
      case 4:
        return `Hole ${car?.Hole} Green`;
      default:
        return "";
    }
  }

  const handleCloseCarModal = () => {
    hideClickOverlay();

    clickWindowState.set(false);
    clickWindowStateCarNumber.set(null);
    setTimeout(() => {
      carNumberHover.set(false);
      setCarPopupClickEvent(null);
    }, 1000);
  }

  const marshalCarStyle = {
    border: paceInfoBorder("border") ? `1px solid ${paceInfoBorder("border")}` : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
    color: paceInfoBorder("border") ? `${paceInfoBorder("border")}` : `${MAP_ICON_COLORS.PACE_NORMAL}`,
    zIndex: "2"
  }

  const specialNeedsStyle = {
    border: paceInfoBorder("border") ? `1px solid ${paceInfoBorder("border")}` : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
    color: paceInfoBorder("border") ? `${paceInfoBorder("border")}` : `${MAP_ICON_COLORS.PACE_NORMAL}`,
    zIndex: "2"
  }

  const foodAndBeverageStyle = {
    border: paceInfoBorder("border") ? `1px solid ${paceInfoBorder("border")}` : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
    color: paceInfoBorder("border") ? `${paceInfoBorder("border")}` : `${MAP_ICON_COLORS.PACE_NORMAL}`,
    zIndex: "2"
  }

  const carParentStyle = () => {
    const isMember = carItem?.Car?.CarType === constCarType.member;
    const carTagged = taggedCarsSaverGlobalState
      .get()
      .includes(carItem?.Car?.CarNumber);

    const borderColor =
      paceInfoBorder("border") ??
      (isMember
        ? MAP_ICON_COLORS.PACE_MEMBER_NORMAL
        : MAP_ICON_COLORS.PACE_NORMAL);

    return {
      background: isMember ? "#2b8c12" : "#1d1d1d",
      border: `${isMember && carTagged ? 5 : 1}px solid ${borderColor}`,
      color: paceInfoBorder("border") ?? MAP_ICON_COLORS.PACE_NORMAL,
    };
  };

  const carStyle = {
    color: paceInfoBorder("border") ? `${paceInfoBorder("border")}` : `${MAP_ICON_COLORS.PACE_NORMAL}`
  }

  const plusStyle = {
    position: "absolute",
    top: 0,
    right: -1
  }

  const renderCarIcon = () => {
    const car = carItem?.Car;

    if (!car) return null;

    if (isStale(carItem?.Car)) {
      return (
        <div className="car-icon">
          <CarNotCommunicatingImage color="red" />
        </div>
      );
    }

    if (showEnergySaverIcon(carItem?.Car)) {
      return (
        <div className="car-icon" style={foodAndBeverageStyle}>
          <img
            src={EnergySaverIcon}
            alt={t("Energy_Saver_Mode")}
            style={{
              width: "16px",
              height: "16px",
              objectFit: "contain",
            }}
          />
        </div>
      );
    }

    const isHandicap = car.CarType === constCarType.handicap ||
      (car.SecondaryCarType === constCarType.handicap &&
        car.CarType === constCarType.member);

    if (car.CarType === constCarType.normal) {
      return null;
    }

    if (car.CarType === constCarType.marshal) {
      return (
        <div className="car-icon" style={marshalCarStyle}>
          <MarshalCarIcon />
        </div>
      );
    }

    if (isHandicap) {
      return (
        <div className="car-icon" style={specialNeedsStyle}>
          <SpecialNeedCarIcons />
        </div>
      );
    }

    if (car.CarType === constCarType.bevcart) {
      return (
        <div className="car-icon" style={foodAndBeverageStyle}>
          <FoodandBeveragesCarIcon />
        </div>
      );
    }

    if (car.CarType === constCarType.outOfService) {
      return (
        <div className="car-icon">
          <OutOfServiceIcon />
        </div>
      );
    }

    return null;
  }

  return (
    <CarWrapper key={uniqueId}>
      <div
        ref={carMarkerRef}
        id={"id" + carItem?.Car?.CarNumber}
        className={checkStyle()}
        style={{ position: "relative" }}
        onClick={(e) => showWindow(e)}
        onMouseEnter={(e) => showHoverOverlay(e)}
        onMouseLeave={hideHoverOverlay}
      >
        {renderCarIcon()}

        <div className="car-number" style={carParentStyle()}>
          <div style={carStyle}>
            {carItem?.Car?.CarNumber}
            <div style={plusStyle}>
              {overlapData?.overlappingDisplay?.length > 1 && (!getCarTracksData.length || !carLocationBtn.get()) && (
                <>+</>
              )}
            </div>
          </div>

          {batteryWarning(carItem?.Car) &&
            <div className="icon-wrapper">
              <BatteryIconWithCircle />
            </div>
          } {batteryCritical(carItem?.Car) &&
            <div className="icon-wrapper">
              <BatteryIconWithCircleRed />
            </div>
          }

          {/* check if car exceed max holes then showing exclamation icon status */}
          {isActionZoneAlert(getCarPathActionZone, carItem?.Car?.ActionZoneId) || carItem?.Car?.MaxHolesExceeded &&
            <div className="icon-wrapper">
              <ExclamationMarkWithRedBg />
            </div>
          }
        </div>
      </div>
    </CarWrapper>
  );
}
);

const TaggedCarIconComponent = ({ uniqueId, paceStatus, GpsiCarId, CarNumber }) => {
  const { tagCarFromFleet, storeFleetTagCarData } = useHookstate(FLEET_GLOBAL_STATES);
  const { t } = useTranslation();
  const [hoverState, setHoverState] = useState(false);
  const [clickState, setClickState] = useState(false);
  const {
    showMessageBoxForSingleCar,
    getSingleCarData,
    tagCarNumber,
    carNumberHover,
  } = useHookstate(MAP_GLOBAL_STATES);

  const { carLocationBtn } = useHookstate(CAR_LOCATION_STATE);

  const setTagCarNumber = (carnumber) => {
    if (tagCarNumber.get().length === 0) {
      tagCarNumber.set((tasks) => [...tasks, carnumber]);
    } else {
      const exists = tagCarNumber.get().find((item) => item === carnumber);
      if (exists) {
        tagCarNumber.set((tasks) => tasks.filter((task) => task !== carnumber));
      } else {
        tagCarNumber.set((tasks) => [...tasks, carnumber]);
      }
    }
  }

  const getDateInfo = () => {
    let dateData =
      storeFleetTagCarData.get() &&
      storeFleetTagCarData.get().LastLocationUpdateInUtc;
    const dateTimeSplitter = dateData && dateData.split("T");
    const dateSplitter = dateTimeSplitter && dateTimeSplitter[0].split("-");
    const monthFetcher = () => {
      switch (dateSplitter && dateSplitter[1]) {
        case "01" || "1":
          return "Jan";
        case "02" || "2":
          return "Feb";
        case "03" || "3":
          return "Mar";
        case "04" || "4":
          return "Apr";
        case "05" || "5":
          return "May";
        case "06" || "6":
          return "Jun";
        case "07" || "7":
          return "Jul";
        case "08" || "8":
          return "Aug";
        case "09" || "9":
          return "Sep";
        case "10":
          return "Oct";
        case "11":
          return "Nov";
        case "12":
          return "Dec";
        default:
          return "No Match";
      }
    };

    const dateFormat = `${dateSplitter && dateSplitter[2]}-${monthFetcher()}-${dateSplitter && dateSplitter[0]
      }`;
    const timeFetcher = dateTimeSplitter && dateTimeSplitter[1].split(".");
    const timeGetter = timeFetcher && timeFetcher[0].split(":");

    const timeFormat = () => {
      let hours = timeGetter && timeGetter[0];
      let minutes = timeGetter && timeGetter[1];
      let ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      minutes = minutes < 10 ? "0" + minutes : minutes;
      let strTime = hours + ":" + minutes + " " + ampm;
      return strTime;
    };
    return `${dateFormat}, ${timeFormat()}`;
  }

  const showWindow = () => {
    if (hoverState) {
      setHoverState(false);
      setClickState(!clickState);
    } else {
      setClickState(!clickState);
      carNumberHover.set(true);
    }
  }

  GpsiCarId = storeFleetTagCarData.get() && storeFleetTagCarData.get().GpsiCarId;
  CarNumber = storeFleetTagCarData.get() && storeFleetTagCarData.get().CarNumber;

  const setCarsData = (e, gpsiCarId, carNo) => {
    e.stopPropagation();
    showMessageBoxForSingleCar.set(true);
    getSingleCarData.set({ GpsiCarId: gpsiCarId, CarNumber: carNo });
  }

  const onMouseEnter = () => {
    setHoverState(true);
    carNumberHover.set(true);
  }

  const onMouseLeave = () => {
    setHoverState(false);
    carNumberHover.set(false);
  }

  const borderColor = () => {
    let border;

    if (storeFleetTagCarData.get().CarNumber) {
      border = "4px solid rgb(95,158,79)";
    } else if (paceStatus === 0) {
      border = "4px solid #FFE500";
    } else if (paceStatus < 0) {
      border = "4px solid #FA3C30";
    } else {
      border = "4px solid #fff";
    }

    return border;
  }

  const handlePaceStatus = () => {
    let paceText = "";
    const carData = storeFleetTagCarData.get();

    if (carData) {
      if (carData.Pace < 0) {
        paceText = "Critical Behind Pace";
      } else if (carData.Pace === 0) {
        paceText = "Behind Pace";
      } else if (carData.Pace > 0) {
        paceText = "Pace ahead";
      }
    }

    return paceText;
  }

  const handleStatus = () => {
    let holeLocationText = "";
    const carData = storeFleetTagCarData.get();

    if (carData) {
      if (carData.HoleLocation === 0) {
        holeLocationText = t("Unknown");
      } else if (carData.HoleLocation === 1) {
        holeLocationText = t("Between_Holes");
      } else if (carData.HoleLocation === 2) {
        holeLocationText = "Tee";
      } else if (carData.HoleLocation === 3) {
        holeLocationText = "Fairway";
      } else if (carData.HoleLocation === 4) {
        holeLocationText = t("Green");
      }
    }

    return holeLocationText;
  }

  return (
    <CarWrapper key={uniqueId}>
      <div className="container" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={showWindow} style={{ border: borderColor() }}>
        <div className="car-icon">
          {tagCarFromFleet.get() && tagCarFromFleet.get()[0]?.cartype === constCarType.normal ? (
            <CarIcon />
          ) : tagCarFromFleet.get() &&
            tagCarFromFleet.get()[0]?.cartype === constCarType.marshal ? (
            <MarshalCarIcon />
          ) : tagCarFromFleet.get() &&
            tagCarFromFleet.get()[0]?.cartype === constCarType.handicap ? (
            <SpecialNeedCarIcons />
          ) : tagCarFromFleet.get() &&
            tagCarFromFleet.get()[0]?.cartype === constCarType.bevcart ? (
            <FoodandBeveragesCarIcon />
          ) : (
            <CarIcon color="black" />
          )}
        </div>
        <div className="car-number">
          {tagCarFromFleet.get() && tagCarFromFleet.get()[0]?.carno}
        </div>
      </div>
      {hoverState && !clickState ? (
        <HoverWindow>
          <div className="wrapper">
            <h1>Car: {" "} {storeFleetTagCarData.get() && storeFleetTagCarData.get().CarNumber}</h1>
            <div className="date-and-time">{getDateInfo()}</div>
            <>
              <span style={{ fontSize: 14 }}>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().Hole === 0
                  ? t("No_cars_available")
                  : `Hole ${storeFleetTagCarData.get().Hole}`}{" "}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>Status - </span>
                {handleStatus()}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>
                  {t("Pace_Status")} 1-{" "}
                </span>
                {handlePaceStatus()}
              </span>
            </>
            <div className="arrow"></div>
          </div>
        </HoverWindow>
      ) : (
        <></>
      )}
      {clickState && (
        <HoverWindow>
          <div className="wrapper">
            <h1>
              {t("Car")}: {" "}
              {storeFleetTagCarData.get() &&
                storeFleetTagCarData.get().CarNumber}
            </h1>
            <div className="date-and-time">{getDateInfo()}</div>
            <>
              <span style={{ fontSize: 14 }}>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().Hole === 0
                  ? t("No_cars_available")
                  : `Hole ${storeFleetTagCarData.get().Hole}`}{" "}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>Status - </span>
                {handleStatus()}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>
                  {t("Pace_Status")} 22 -{" "}
                </span>
                {handlePaceStatus()}
              </span>
            </>
            <div className="icon-wrapper">
              <span onClick={(e) => setCarsData(e, GpsiCarId, CarNumber)}>
                <ChatIcon width={18} height={14} color="var(--text-primary)" />
              </span>
              <span>
                <ExclamationMarkWhiteIcon color="var(--text-primary)" />
              </span>
              <span onClick={() => setTagCarNumber(CarNumber)}>
                <TagIcon color="var(--text-primary)" />
              </span>
              <span onClick={() => carLocationBtn.set(!carLocationBtn.get())}>
                <LocationPointIcon color="var(--text-primary)" />
              </span>
            </div>
            <div className="arrow"></div>
          </div>
        </HoverWindow>
      )}
    </CarWrapper>
  );
};

export {
  MapHoleMarker,
  MapCarIcon,
  MapCenterMarker,
  TaggedCarIconComponent,
  MapPointMarker,
  MapTeesMarker,
};